import React, { ReactElement } from 'react';
import { VirtualPortal, VirtualPortalContent } from '../types';
import { createVirtualPortal } from '..';

/**
 * Create a new component that is a hub for which a collection of portals will be created with the given names, and within which the created portals' channels will be open.
 *
 * Note that portals created this way don't strictly need to be used within
 * the hub, but the hub can be a convenient way to open the channels for any of
 * the portals contained within.
 *
 * @param portalNames Names of the portals to be created. Use PascalCase names.
 * @return
 *  A hybrid object that may be used as a React component or as a namespace.
 *  When used as React component, the children inside will be in a context that
 *  is suitable to send and receive between any of the portals that were named.
 *  When used as a namespace, any of the portal names given become properties
 *  of the namespace and will refer to the portals, which can then be used to
 *  access the <.Send> and <.Receive> components.
 * @example
 * ```jsx
 * const MyHub = createVirtualPortalHub('Foo', 'Bar');
 *
 * const el = <MyHub>
 *   <div>
 *     <MyHub.Foo.Receive />
 *   </div>
 *   <MyHub.Foo.Send><div>...Some content...</div></MyHub.Foo.Send>
 *   <div>
 *     <MyHub.Bar.Receive />
 *   </div>
 *   <MyHub.Bar.Send><span>...Some more content...</span></MyHub.Foo.Send>
 * </MyHub>;
 * ```
 */
const createVirtualPortalHub = <
    N0 extends string = never, P0 extends object = object,
    N1 extends string = never, P1 extends object = object,
    N2 extends string = never, P2 extends object = object,
    N3 extends string = never, P3 extends object = object,
    N4 extends string = never, P4 extends object = object,
    N5 extends string = never, P5 extends object = object,
    N6 extends string = never, P6 extends object = object,
    N7 extends string = never, P7 extends object = object,
    N8 extends string = never, P8 extends object = object,
    N9 extends string = never, P9 extends object = object,
    N10 extends string = never, P10 extends object = object,
    N11 extends string = never, P11 extends object = object,
    N12 extends string = never, P12 extends object = object,
    N13 extends string = never, P13 extends object = object,
    N14 extends string = never, P14 extends object = object,
    N15 extends string = never, P15 extends object = object,
>(...portalNames: [
    N0?, N1?, N2?, N3?,
    N4?, N5?, N6?, N7?,
    N8?, N9?, N10?, N11?,
    N12?, N13?, N14?, N15?
]) => {

    const portalMap: (
        {[K in N0]?: VirtualPortal<P0>} &
        {[K in N1]?: VirtualPortal<P1>} &
        {[K in N2]?: VirtualPortal<P2>} &
        {[K in N3]?: VirtualPortal<P3>} &
        {[K in N4]?: VirtualPortal<P4>} &
        {[K in N5]?: VirtualPortal<P5>} &
        {[K in N6]?: VirtualPortal<P6>} &
        {[K in N7]?: VirtualPortal<P7>} &
        {[K in N8]?: VirtualPortal<P8>} &
        {[K in N9]?: VirtualPortal<P9>} &
        {[K in N10]?: VirtualPortal<P10>} &
        {[K in N11]?: VirtualPortal<P11>} &
        {[K in N12]?: VirtualPortal<P12>} &
        {[K in N13]?: VirtualPortal<P13>} &
        {[K in N14]?: VirtualPortal<P14>} &
        {[K in N15]?: VirtualPortal<P15>}
    ) = {};

    portalNames.forEach((name: string) => {
        portalMap[name] = createVirtualPortal();
    });

    const portals: VirtualPortal[] = Object.values(portalMap);

    return Object.assign(
        (props: VirtualPortalContent) => portals.reduce(
            (children, Portal) => <Portal.Channel>{children}</Portal.Channel>,
                props.children as ReactElement
        ),
        portalMap
    );
};

export default createVirtualPortalHub;
